import React, { useContext } from "react";
import { PersonalDataType, ValidationType } from "../../types";
import { StoreContext } from "../context/Store";
import Input from "../parts/Input";
import Text from "../parts/Text";
import {
  formatPostal,
  validateEmail,
  validateLength,
  validatePhone,
  validatePostal,
} from "../utils";

export interface PersonalDataProps {}

export interface PersonalDataState {}

export const validation: ValidationType<PersonalDataType> = {
  name: {
    valid: (value: string) => validateLength(value),
    required: true,
  },
  lastName: {
    valid: (value: string) => validateLength(value),
    required: true,
  },
  street: {
    valid: (value: string) => validateLength(value),
    required: false,
  },
  city: {
    valid: (value: string) => validateLength(value),
    required: false,
  },
  postal: {
    valid: (value: string) => validatePostal(value),
    required: false,
  },
  phone: {
    valid: (value: string) => validatePhone(value),
    required: true,
  },
  email: {
    valid: (value: string) => validateEmail(value),
    required: true,
  },
};

export const PersonalData = React.forwardRef<HTMLDivElement, PersonalDataProps>(
  (props, ref) => {
    const store = useContext(StoreContext);
    const { setPersonalData } = store;
    const { name, lastName, street, city, postal, phone, email } =
      store.personalData;

    const change = (e: React.ChangeEvent<HTMLInputElement>) => {
      setPersonalData(e.target.name as keyof PersonalDataType, e.target.value);
    };

    const changePostal = (e: React.ChangeEvent<HTMLInputElement>) => {
      setPersonalData(
        e.target.name as keyof PersonalDataType,
        formatPostal(e.target.value, postal)
      );
    };

    return (
      <section id="personalData" className="mb-5" ref={ref}>
        <Text tag="h2" color="pink" classes="mb-5">
          Dane osobowe
        </Text>
        <Text tag="h4" color="blue" classes="mb-2">
          Czas, by uzupełnić kolejne rubryki w CV. <br /> Uważaj, by niczego nie
          pominąć.
        </Text>
        <Text tag="p" classes="mb-5">
          Wszystkie informacje dla przyszłego pracodawcy mogą być cenne.
        </Text>
        <Text tag="h4" color="blue" classes="mb-2">
          CV najlepiej zacząć od wpisania danych osobowych,
        </Text>
        <Text tag="p" classes="mb-5">
          takich jak imię i nazwisko oraz dane kontaktowe. To informacje
          niezbędne dla osoby, która będzie się chciała z Tobą skontaktować.
          Pamiętaj, że nawet jedna literówka może utrudnić z Tobą kontakt.
          Szkoda, by ominęła Cię ciekawa propozycja pracy :-)
        </Text>
        <Text tag="h4" classes="mb-5" color="blue">
          Podawane przez Ciebie dane w generatorze nie są zapisywane na tej
          stronie
        </Text>
        <div className="row">
          <div className="col-12 col-sm-6">
            <Input
              name="name"
              label="Imię"
              onChange={change}
              value={name}
              required={validation.name.required}
              isValid={validation.name.valid(name)}
            />
          </div>
          <div className="col-12 col-sm-6">
            <Input
              name="lastName"
              label="Nazwisko"
              onChange={change}
              value={lastName}
              required={validation.lastName.required}
              isValid={validation.lastName.valid(lastName)}
            />
          </div>
        </div>
        <div className="row">
          <div className="col-12">
            <Input
              name="street"
              label="Ulica"
              onChange={change}
              value={street}
              required={validation.street.required}
              isValid={validation.street.valid(street)}
            />
          </div>
        </div>
        <div className="row">
          <div className="col-12 col-sm-8">
            <Input
              name="city"
              label="Miasto"
              onChange={change}
              value={city}
              required={validation.city.required}
              isValid={validation.city.valid(city)}
            />
          </div>
          <div className="col-12 col-sm-4">
            <Input
              name="postal"
              label="Kod pocztowy"
              onChange={changePostal}
              value={postal}
              required={validation.postal.required}
              isValid={validation.postal.valid(postal)}
              placeholder="__-___"
            />
          </div>
        </div>
        <div className="row">
          <div className="col-12 col-sm-6">
            <Input
              name="phone"
              label="Telefon komórkowy"
              onChange={change}
              value={phone}
              required={validation.phone.required}
              isValid={validation.phone.valid(phone)}
              type="text"
            />
          </div>
          <div className="col-12 col-sm-6">
            <Input
              name="email"
              label="Adres e-mail"
              onChange={change}
              value={email}
              required={validation.email.required}
              isValid={validation.email.valid(email)}
            />
          </div>
        </div>
      </section>
    );
  }
);

export default PersonalData;
